import React from 'react';
import { useQuery } from 'react-query';
import { useNavigate, useParams } from 'react-router-dom';

import styled from 'styled-components';

import { getAlertDetails } from 'apis/AlertsAPI';
import Avatar from 'common/Avatar';
import ChevronRight from 'common/Icons/ChevronRight';
import { useSidebarMenuLogic } from 'components/layout/hooks';
import ErrorMessage from 'components/lib/ErrorMessage';
import LoaderRibbed from 'components/lib/LoaderRibbed';
import { ALERT_TYPE } from 'constants/alerts';
import { getFormattedDate, getRelativeTime } from 'helpers/dates';
import { AlertRelatedCommunityType } from 'types/alerts';

import AlertActionPlanCard from './AlertActionPlanCard';
import AlertsHeader from './AlertsHeader';
import AlertsIncentivesTable from './AlertsIncentivesTable';
import AlertsPricingTable from './AlertsPricingTable';
import AlertsReviewsTable from './AlertsReviewsTable';
import AlertTypeTag from './AlertTypeTag';
import { ACTOR_TYPE } from '../../constants';

const Container = styled.div`
  width: 100%;
`;

const Main = styled.div`
  display: flex;
  flex-direction: column;
  gap: 30px;
  padding: 10px 30px 20px 30px;
`;

const DetailsCard = styled.div`
  border: 1px solid var(--line-gray);
  border-radius: 10px;
  padding: 20px;
  display: flex;
  justify-content: space-between;
  gap: 20px;
`;

const LeftContent = styled.div`
  display: flex;
  gap: 20px;
  flex: 1;
`;

const BackIcon = styled.div`
  width: 40px;
  height: 40px;
  min-width: 40px;
  border-radius: 4px;
  border: 1px solid var(--line-gray);
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
`;

const Content = styled.div`
  min-width: 0;
  width: 100%;
`;

const Title = styled.div`
  font-size: 20px;
  font-family: var(--font-bold);
`;

const MetaRow = styled.div`
  margin-top: 8px;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const MetaDate = styled.span`
  font-size: 12px;
  color: var(--text-secondary);
`;

const InsightHeader = styled.div`
  margin-top: 20px;
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 16px;
  font-family: var(--font-bold);
`;

const InsightIcon = styled.img`
  width: 20px;
  height: 20px;
`;

const Description = styled.div`
  margin-top: 5px;
  font-size: 14px;
  color: var(--text-secondary);
`;

const Footer = styled.div`
  margin-top: 20px;
  display: flex;
  align-items: center;
  gap: 12px;
`;

const RightPanel = styled.div`
  position: relative;
  padding-left: 20px;

  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 1px;
    background: linear-gradient(to bottom, #fafafa 0%, var(--line-gray) 50%, #fafafa 100%);
  }
`;

const PanelTitle = styled.div`
  font-size: 10px;
  letter-spacing: 2px;
  color: var(--text-secondary);
  margin-bottom: 10px;
`;

const CommunitiesList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const CommunityRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
`;

const CommunityAvatar = styled(Avatar)`
  flex-shrink: 0;
`;

const CommunityInfo = styled.div`
  min-width: 0;
  display: flex;
  flex-direction: column;
`;

const CommunityName = styled.div`
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 2px;
`;

const CommunityType = styled.div`
  font-size: 12px;
  color: var(--text-secondary);
`;

const LoaderContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 100px;
`;

const AlertsDetails = () => {
  const navigate = useNavigate();
  const { alertId } = useParams();
  const { showActionPlans } = useSidebarMenuLogic();
  const alertIdNumber = Number(alertId);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['alert-details', alertId],
    queryFn: () => getAlertDetails(alertIdNumber),
    enabled: Number.isFinite(alertIdNumber) && alertIdNumber > 0,
    refetchOnWindowFocus: false,
  });

  const communities: AlertRelatedCommunityType[] = data?.related_communities ?? [];
  const incentivesCommunityId = communities?.[0]?.id;
  const isIncentivesAlert = data?.type_id === ALERT_TYPE.INCENTIVES;
  const isPricingAlert = data?.type_id === ALERT_TYPE.PRICING;
  const isReviewsAlert =
    data?.type_id === ALERT_TYPE.COMMUNITY_REVIEWS || data?.type_id === ALERT_TYPE.COMMPETITORS_REVIEWS;

  return (
    <Container data-test-id="alerts-details-page">
      <AlertsHeader />
      <Main data-test-id="alerts-details-main">
        <div data-test-id="alerts-details-error-message">
          <ErrorMessage text="Failed to load alert details. Please try again." active={isError} />
        </div>

        {isLoading && (
          <LoaderContainer>
            <LoaderRibbed active />
          </LoaderContainer>
        )}

        {!isLoading && !isError && data && (
          <>
            <DetailsCard data-test-id="alerts-details-card">
              <LeftContent>
                <BackIcon data-test-id="alerts-details-back-button" onClick={() => navigate(-1)}>
                  <img src="/icons/chevron-left-black.svg" alt="back" width="24px" height="24px" />
                </BackIcon>

                <Content>
                  <Title>{data.title}</Title>
                  <MetaRow>
                    {data.type_name && <AlertTypeTag typeName={data.type_name} />}
                    <MetaDate>
                      {getRelativeTime(data.created_at)} · {getFormattedDate(data.created_at, 'MMM D YYYY')}
                    </MetaDate>
                  </MetaRow>

                  <InsightHeader>
                    <InsightIcon src="/icons/ai-blob.svg" alt="ai-insights" />
                    <span>AI Insights</span>
                  </InsightHeader>
                  <Description>{data.description}</Description>

                  {data.action_plan_id && (
                    <Footer>
                      <AlertActionPlanCard
                        actionPlanName={data.action_plan_name}
                        disabled={!showActionPlans}
                        data-test-id="alerts-details-action-plan-card"
                        onClick={() => {
                          if (!showActionPlans) {
                            return;
                          }
                          navigate(`/community/${data.community_id}/action-plans/${data.action_plan_id}`);
                        }}
                      />
                    </Footer>
                  )}
                </Content>
              </LeftContent>

              <RightPanel>
                <PanelTitle>COMMUNITIES</PanelTitle>
                <CommunitiesList>
                  {communities.map((community, index) => {
                    const isMyCommunity = community.id === data.community_id;

                    return (
                      <CommunityRow
                        key={`${community.id}-${index}`}
                        onClick={() => navigate(`/community/${data.community_id}/competitor/${community.id}/info`)}
                      >
                        <CommunityAvatar size={30} name={community.comunity_name || '?'} type={ACTOR_TYPE.COMPETITOR} />
                        <CommunityInfo>
                          <CommunityName>
                            <span>{community.comunity_name}</span>
                            <ChevronRight fill="var(--text-primary)" width={18} height={18} />
                          </CommunityName>
                          <CommunityType>{isMyCommunity ? 'My Community' : 'Competitor'}</CommunityType>
                        </CommunityInfo>
                      </CommunityRow>
                    );
                  })}
                </CommunitiesList>
              </RightPanel>
            </DetailsCard>

            {isIncentivesAlert && incentivesCommunityId && (
              <AlertsIncentivesTable communityId={incentivesCommunityId} highlightedIncentiveId={data.reference_id} />
            )}
            {isPricingAlert && <AlertsPricingTable alertId={alertIdNumber} />}
            {isReviewsAlert && <AlertsReviewsTable alertId={alertIdNumber} />}
          </>
        )}
      </Main>
    </Container>
  );
};

export default AlertsDetails;
